/**
 * Category-owned storage manifest and adapter (MTN-1444).
 *
 * Consent attaches to a value's lifecycle, not just its writer: every
 * MTN-owned browser-storage key is listed here with its Termly category,
 * stores, scope, and lifetime, matching the MTN-1434 decision addendum.
 * Reads of a denied category return nothing; a restrictive consent change
 * purges every non-essential entry (hook registered with the MTN-1443
 * revocation lifecycle, which also runs it once at page init).
 *
 * The manifest is the single source of truth: the storage-manifest scan test
 * fails when a writer uses a key that is not listed, and the adapter throws
 * for unlisted keys at runtime.
 */

import storage, { isSupported } from "local-storage-fallback";

import { registerPurgeHook } from "./consent-lifecycle";
import { remove as removeCookie, setIfChanged } from "./cookies";
import { ADVERTISING_COOKIES, CATEGORIES, hasCategory, ready } from "./termly";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Stores: "cookie" (document.cookie), "local" (localStorage; the migration
 * marker goes through local-storage-fallback),
 * "session" (sessionStorage), "localforage" (IndexedDB via localforage).
 *
 * `lifetime` documents the disposition window an entry's own enforcer
 * implements: `lifetimeDays` makes the adapter wrap localforage and
 * localStorage values in a `{t, v}` envelope and expire them on read; each
 * write site enforces its cookie lifetime and `scope` (host-only vs parent
 * domain) through util/cookies.js (MTN-321); the modal entries' windows are
 * re-show windows enforced by modal-lifecycle date math, so the stored
 * timestamp itself persists.
 *
 * Essential entries are never purged; everything else is deleted whenever
 * its category is denied.
 */
export const STORAGE_MANIFEST = Object.freeze(
  [
    // --- Essential -------------------------------------------------------
    // Termly consent-record enforcement bridge (MTN-1439 disposition:
    // enforcement-only, never sent to vendors).
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-termly-uuid",
      lifetime: "90d",
      scope: "parent",
      stores: ["cookie"],
    },
    // Versioned consent envelope for edge consumers (MTN-1439) — consent
    // metadata only, written by util/consent-envelope.js under the key
    // exported from @moneytalksnews/privacy-shared.
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-consent-envelope",
      lifetime: "session",
      scope: "parent",
      stores: ["cookie"],
    },
    // Consent mirror read by the PHP short-link redirect (see below).
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-consent-categories",
      lifetime: "90d",
      scope: "host",
      stores: ["cookie"],
    },
    // Chunk-load and consent-reload loop guards.
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-chunk-reload-attempted",
      lifetime: "tab",
      scope: "host",
      stores: ["session"],
    },
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-consent-reload",
      lifetime: "tab",
      scope: "host",
      stores: ["session"],
    },
    // One-shot migration marker for this module.
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-storage-migration",
      lifetime: "persistent",
      scope: "host",
      stores: ["local"],
    },
    // Address-free newsletter signup handoff, consumed by /thank-you/;
    // replacement with a server token is MTN-1453.
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-newsletter-signup-completion",
      lifetime: "tab",
      scope: "host",
      stores: ["session"],
    },
    // Functional flags honoring the visitor's own choice (ratified
    // 2026-08-12): no identity, no cross-context tracking, retention-capped.
    // Subscriber status refreshes on a confirmed subscription or a
    // newsletter-link arrival (revised 2026-08-13 — see newsletter.js). In
    // localStorage so the head.blade.php snippet can read it before first
    // paint (MTN-1825); that snippet repeats lifetimeDays.
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-subscriber-status",
      lifetime: "13m",
      lifetimeDays: 396,
      scope: "origin",
      stores: ["local"],
    },
    // Freeze membership eligibility before this visit can learn subscriber status.
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-subscriber-visit-status",
      lifetime: "tab",
      scope: "origin",
      stores: ["session"],
    },
    // Modal suppression windows are enforced by modal-lifecycle date math
    // (newsletter 90d, membership 7d), so no adapter envelope here.
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-newsletter-modal",
      lifetime: "90d",
      scope: "origin",
      stores: ["session", "localforage"],
    },
    {
      category: CATEGORIES.ESSENTIAL,
      key: "mtn-membership-modal",
      lifetime: "7d",
      scope: "origin",
      stores: ["session", "localforage"],
    },

    // --- Performance -----------------------------------------------------
    // Recommender identifier (MTN-1445): minted by the head snippet (gated
    // on this module's consent mirror) or util/mtn-id.js on an in-page
    // grant; the theme sends it only to the ranking endpoints, transiently.
    // Parent scope because the Worker pipelines (Tune-Out, Newsletter,
    // Ledger, DSR) still read/mint the cookie server-side until their own
    // MTN-1434 follow-ups land. Listing it here makes the purge machinery
    // delete it on Performance withdrawal.
    {
      category: CATEGORIES.PERFORMANCE,
      key: "mtn-id",
      lifetime: "13m",
      scope: "parent",
      stores: ["cookie"],
    },
    // Read-history maps ({id: last-seen ms}, path/ID only, expiry and caps
    // enforced by ledger.js).
    {
      category: CATEGORIES.PERFORMANCE,
      key: "mtn-read-articles",
      lifetime: "30d/200 entries",
      scope: "origin",
      stores: ["localforage"],
    },
    {
      category: CATEGORIES.PERFORMANCE,
      key: "mtn-read-blocks",
      lifetime: "30d",
      scope: "origin",
      stores: ["localforage"],
    },
    // Recommendation attribution, consumed on the next page load.
    {
      category: CATEGORIES.PERFORMANCE,
      key: "mtn-recid",
      lifetime: "one navigation",
      scope: "host",
      stores: ["session"],
    },

    // --- Analytics -------------------------------------------------------
    // Host-only since MTN-321 (Tune-Out reads it on the page's own host);
    // legacy parent-domain copies age out.
    {
      category: CATEGORIES.ANALYTICS,
      key: "mtn-posthog-distinct-id",
      lifetime: "91d",
      scope: "host",
      stores: ["cookie"],
    },
    // Legacy GA mirrors; removal in favor of native Consent Mode is MTN-319.
    {
      category: CATEGORIES.ANALYTICS,
      key: "mtn-ga4-client-id",
      lifetime: "session",
      scope: "host",
      stores: ["cookie"],
    },
    {
      category: CATEGORIES.ANALYTICS,
      key: "mtn-ga4-session-id",
      lifetime: "session",
      scope: "host",
      stores: ["cookie"],
    },
    // Newsletter signup attribution record (separate from the Advertising
    // cookies below).
    {
      category: CATEGORIES.ANALYTICS,
      key: "mtn-newsletter-utm",
      lifetime: "7d",
      lifetimeDays: 7,
      scope: "origin",
      stores: ["localforage"],
    },

    // --- Advertising -----------------------------------------------------
    // Cross-page affiliate attribution (Tune links and PHP short links);
    // host-only since MTN-321 — every consumer reads from the page's host.
    {
      category: CATEGORIES.ADVERTISING,
      key: "mtn-utm-source",
      lifetime: "7d",
      scope: "host",
      stores: ["cookie"],
    },
    {
      category: CATEGORIES.ADVERTISING,
      key: "mtn-utm-medium",
      lifetime: "7d",
      scope: "host",
      stores: ["cookie"],
    },
    {
      category: CATEGORIES.ADVERTISING,
      key: "mtn-utm-campaign",
      lifetime: "7d",
      scope: "host",
      stores: ["cookie"],
    },
    {
      category: CATEGORIES.ADVERTISING,
      key: "mtn-utm-content",
      lifetime: "7d",
      scope: "host",
      stores: ["cookie"],
    },
    {
      category: CATEGORIES.ADVERTISING,
      key: "mtn-utm-term",
      lifetime: "7d",
      scope: "host",
      stores: ["cookie"],
    },
    // First-party ad click-ID copies: 30-day cap per the MTN-1434
    // disposition (MTN-321). Host-only, except _fbp/_fbc, which keep the
    // parent domain for Meta's eTLD+1 matching convention — and whose cap
    // governs only our pre-seed copies; Meta's pixel re-mints them on its
    // own 90-day schedule once Advertising consent lets it load (see
    // routes/common.js).
    ...ADVERTISING_COOKIES.map((key) => ({
      category: CATEGORIES.ADVERTISING,
      key,
      // "seed": MTN's cap applies until Meta's pixel re-mints on its own
      // 90-day schedule (see above).
      lifetime: ["_fbp", "_fbc"].includes(key) ? "30d seed / 90d Meta" : "30d",
      scope: ["_fbp", "_fbc"].includes(key) ? "parent" : "host",
      stores: ["cookie"],
    })),
  ].map(Object.freeze)
);

const requireEntry = (key) => {
  const entry = STORAGE_MANIFEST.find((candidate) => candidate.key === key);

  if (!entry) {
    throw new Error(`Storage key "${key}" is not in the storage manifest`);
  }

  return entry;
};

const allowed = (entry) =>
  entry.category === CATEGORIES.ESSENTIAL || hasCategory(entry.category);

// "local" entries go to localStorage directly, JSON-encoded, rather than
// through local-storage-fallback, which would store them in a cookie where
// web storage is blocked.
const localStore = {
  getItem: (key) => JSON.parse(localStorage.getItem(key)),
  removeItem: (key) => localStorage.removeItem(key),
  setItem: (key, value) => localStorage.setItem(key, JSON.stringify(value)),
};

const storeFor = (entry) =>
  entry.stores.includes("local") ? localStore : import("localforage");

// localforage rejects every call when the browser has no usable driver ("No
// available storage method found.") and IndexedDB operations can fail on
// their own, as can localStorage. Like the sessionStorage helpers below, the
// adapter treats a failed store as empty: reads return null and writes and
// removes do nothing (MTN-1812). Imports stay outside the guard so a
// stale-chunk failure still rejects to the caller.

/**
 * Read a manifest-listed localforage or localStorage value. Returns null when
 * the entry's category is denied or the value's lifetime has passed.
 */
export const getItem = async (key) => {
  const entry = requireEntry(key);

  if (!allowed(entry)) {
    return null;
  }

  const store = await storeFor(entry);

  try {
    const stored = await store.getItem(key);

    if (stored === null || stored === undefined) {
      return null;
    }

    if (!entry.lifetimeDays) {
      return stored;
    }

    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript JSON/storage boundary rejects primitives while preserving the surrounding null and array guards.
    if (stored && typeof stored === "object" && "v" in stored) {
      // No timestamp means no enforceable cap, so it counts as expired.
      if (
        // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript data boundary requires a primitive number; coercion or finiteness checks change NaN/Infinity semantics.
        typeof stored.t !== "number" ||
        Date.now() - stored.t > entry.lifetimeDays * DAY_MS
      ) {
        await store.removeItem(key);

        return null;
      }

      return stored.v;
    }

    // Raw localforage value written by a pre-manifest bundle during a deploy
    // transition; the next write converts it to the envelope shape.
    return stored;
  } catch {
    return null;
  }
};

/**
 * Write a manifest-listed localforage or localStorage value. No-op when the
 * entry's category is denied — re-checked after the await, so a revocation
 * that lands while the write is in flight drops it (MTN-1443).
 * window.termlyData updates synchronously before the consent event
 * dispatches, so this re-check sees the revocation the instant it exists.
 */
export const setItem = async (key, value) => {
  const entry = requireEntry(key);

  if (!allowed(entry)) {
    return;
  }

  const store = await storeFor(entry);

  if (!allowed(entry)) {
    return;
  }

  try {
    await store.setItem(
      key,
      entry.lifetimeDays ? { t: Date.now(), v: value } : value
    );
  } catch {
    // Store unavailable — the write is dropped.
  }
};

export const removeItem = async (key) => {
  const store = await storeFor(requireEntry(key));

  try {
    await store.removeItem(key);
  } catch {
    // Store unavailable — nothing to remove.
  }
};

/**
 * Synchronous sessionStorage twins for values written on the click path
 * (mtn-recid must land before navigation).
 */
export const getSessionItem = (key) => {
  const entry = requireEntry(key);

  if (!allowed(entry)) {
    return null;
  }

  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

export const setSessionItem = (key, value) => {
  const entry = requireEntry(key);

  if (!allowed(entry)) {
    return;
  }

  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, policy) — nothing to enforce.
  }
};

export const removeSessionItem = (key) => {
  requireEntry(key);

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable — nothing to remove.
  }
};

/**
 * Can this browser keep a value across pageviews, in sessionStorage or a
 * working localforage driver? Callers skip UI whose dismissal nothing could
 * remember (MTN-1816).
 */
export const canPersist = async () => {
  if (isSupported("sessionStorage")) {
    return true;
  }

  const localforage = await import("localforage");

  try {
    await localforage.ready();

    return true;
  } catch {
    return false;
  }
};

const purgeEntry = async (entry) => {
  for (const store of entry.stores) {
    if (store === "cookie") {
      // removeCookie covers both scopes every writer has used; extra
      // attempts are no-ops.
      removeCookie(entry.key);
    } else if (store === "session") {
      try {
        sessionStorage.removeItem(entry.key);
      } catch {
        // Storage unavailable — nothing stored, nothing to purge.
      }
    } else if (store === "local") {
      storage.removeItem(entry.key);
    }
  }

  if (entry.stores.includes("localforage")) {
    const localforage = await import("localforage");

    try {
      await localforage.removeItem(entry.key);
    } catch {
      // Store unavailable — nothing stored, nothing to purge.
    }
  }
};

/**
 * Delete every non-essential entry whose category is currently denied.
 * Idempotent: runs on every restrictive consent change and once at page
 * init (via the MTN-1443 purge-hook registry).
 */
export const purgeDenied = async () => {
  const denied = STORAGE_MANIFEST.filter(
    (entry) =>
      entry.category !== CATEGORIES.ESSENTIAL && !hasCategory(entry.category)
  );

  await Promise.all(denied.map(purgeEntry));
};

// --- Consent mirror for the PHP short-link redirect ------------------------
// links.php forwards consent-dependent cookies only when this mirror
// explicitly lists advertising; no mirror means forward nothing. The mirror
// can only restrict relative to the pre-MTN-1444 behavior (unconditional
// forwarding); Termly stays the consent authority, and MTN-1447 removes
// short-link cookie recovery entirely.
const CONSENT_COOKIE = "mtn-consent-categories";

const writeConsentCookie = () => {
  if (window.termlyData === null) {
    // Unresolved is not a record: keep the previous mirror.
    return;
  }

  const categories = (window.termlyData?.categories || []).join(",");
  // setIfChanged: a page load alone must not renew the mirror's 90-day
  // retention (MTN-321); it rewrites on a consent change or after expiry.
  setIfChanged(CONSENT_COOKIE, categories, { days: 90 });
};

// --- One-time disposition migrations (MTN-1434 addendum) --------------------

const MIGRATION_KEY = "mtn-storage-migration";
const MIGRATION_VERSION = "1";

// Article ids arrive as absolute URLs; the disposition allows path/ID only.
export const normalizeReadId = (id) => {
  try {
    return new URL(id).pathname;
  } catch {
    return String(id);
  }
};

const READ_HISTORY_MAX_ENTRIES = 200;
const READ_HISTORY_MAX_AGE_DAYS = 30;

export const pruneReadHistory = (map, now = Date.now()) => {
  const fresh = Object.entries(map).filter(
    ([, t]) => now - t <= READ_HISTORY_MAX_AGE_DAYS * DAY_MS
  );

  fresh.sort(([, a], [, b]) => b - a);

  return Object.fromEntries(fresh.slice(0, READ_HISTORY_MAX_ENTRIES));
};

const migrateReadHistory = async (localforage, oldKey, newKey) => {
  const old = await localforage.getItem(oldKey);

  if (
    Array.isArray(old) &&
    old.length > 0 &&
    hasCategory(CATEGORIES.PERFORMANCE)
  ) {
    const now = Date.now();
    const map = {};

    for (const id of old) {
      map[normalizeReadId(id)] = now;
    }

    await localforage.setItem(newKey, pruneReadHistory(map, now));
  }

  await localforage.removeItem(oldKey);
};

const LEGACY_UTM_KEYS = [
  "mtn-utm-source",
  "mtn-utm-medium",
  "mtn-utm-campaign",
  "mtn-utm-content",
  "mtn-utm-term",
];

const migrateUtmRecord = async (localforage) => {
  // mtn-utm-last-updated held unix seconds; only a record still inside the
  // 7-day attribution window is worth carrying over.
  const updated = await localforage.getItem("mtn-utm-last-updated");

  const fresh =
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript data boundary requires a primitive number; coercion or finiteness checks change NaN/Infinity semantics.
    typeof updated === "number" && Date.now() - updated * 1000 <= 7 * DAY_MS;

  if (fresh && hasCategory(CATEGORIES.ANALYTICS)) {
    const reads = await Promise.allSettled(
      LEGACY_UTM_KEYS.map((key) => localforage.getItem(key))
    );

    const values = {};

    // Drain every read, then preserve legacy field order and error precedence.
    for (const [index, result] of reads.entries()) {
      if (result.status === "rejected") {
        throw result.reason;
      }

      const { value } = result;

      if (value) {
        values[LEGACY_UTM_KEYS[index].replace("mtn-utm-", "")] = value;
      }
    }

    if (Object.keys(values).length > 0) {
      await localforage.setItem("mtn-newsletter-utm", {
        t: updated * 1000,
        v: values,
      });
    }
  }

  for (const key of [...LEGACY_UTM_KEYS, "mtn-utm-last-updated"]) {
    // oxlint-disable-next-line no-await-in-loop -- Stop legacy-key cleanup on failure so the migration marker stays unset.
    await localforage.removeItem(key);
  }
};

const SUBSCRIBER_KEY = "mtn-subscriber-status";

// The subscriber flag moved from IndexedDB to localStorage (MTN-1825). Carry
// a subscribed flag across with its timestamp; one without (a raw
// "subscribed" from a pre-manifest bundle) starts its 13-month cap now.
// Anything else is dropped.
// ponytail: an IndexedDB read on every load; delete after November 2027,
// once every IndexedDB copy is past its 396-day cap.
const moveSubscriberFlag = async (localforage) => {
  const stored = await localforage.getItem(SUBSCRIBER_KEY);

  if (stored === null) {
    return;
  }

  const subscribed = stored === "subscribed" || stored?.v === "subscribed";

  if (subscribed && localStorage.getItem(SUBSCRIBER_KEY) === null) {
    localStore.setItem(SUBSCRIBER_KEY, {
      // oxlint-disable-next-line anti-slop/no-runtime-typeof -- This JavaScript data boundary requires a primitive number; coercion or finiteness checks change NaN/Infinity semantics.
      t: typeof stored.t === "number" ? stored.t : Date.now(),
      v: "subscribed",
    });
  }

  await localforage.removeItem(SUBSCRIBER_KEY);
};

// Runs before the modal/newsletter modules read their stores, so moved and
// converted values are in place on the same page load (routes/common.js
// holds the newsletter and membership inits until it settles).
// Idempotent: the subscriber move runs every load, and the rest reruns
// harmlessly until migrateConsentGated sets the marker (which waits for
// Termly, so a Termly-blocked visitor reruns this part).
const migrateImmediate = async () => {
  const localforage = await import("localforage");

  try {
    await moveSubscriberFlag(localforage);
  } catch {
    // Store unavailable — the flag stays put and the next load retries.
  }

  if (storage.getItem(MIGRATION_KEY) === MIGRATION_VERSION) {
    return;
  }

  // Removed outright by the disposition table; aggregate PostHog/Plausible
  // reporting replaces them.
  for (const key of ["mtn-page-views", "mtn-topics", "mtn-categories"]) {
    storage.removeItem(key);
  }

  try {
    localStorage.removeItem("clicks_debug");
  } catch {
    // Storage unavailable — nothing to remove.
  }

  try {
    // Newsletter modal seen-flag: legacy `true` → timestamp, so the new
    // 90-day re-show window has date math to work from (an unparseable value
    // counts as "not seen" and would re-show the modal to everyone).
    const newsletterSeen = await localforage.getItem("mtn-newsletter-modal");

    if (newsletterSeen === true) {
      await localforage.setItem(
        "mtn-newsletter-modal",
        new Date().toISOString()
      );
    }
  } catch {
    // Store unavailable — skip the conversions.
  }
};

// Copies of consent-gated data must wait for Termly to resolve — copying
// while unresolved would drop a consenting visitor's data (hasCategory is
// false until Termly reports).
const migrateConsentGated = async () => {
  if (storage.getItem(MIGRATION_KEY) === MIGRATION_VERSION) {
    return;
  }

  const localforage = await import("localforage");

  try {
    await migrateReadHistory(localforage, "articles", "mtn-read-articles");
    await migrateReadHistory(localforage, "blocks", "mtn-read-blocks");
    await migrateUtmRecord(localforage);
  } catch {
    // Store unavailable — leave the marker unset so a later load retries.
    return;
  }

  storage.setItem(MIGRATION_KEY, MIGRATION_VERSION);
};

let initialized = false;

/**
 * Wire the manifest into the page: write the consent mirror cookie (and
 * rewrite it on every Termly update), register the revoke/init purge hook,
 * and run the one-time disposition migrations. routes/common.js holds the
 * newsletter and membership inits until this settles.
 */
export const initConsentStorage = async () => {
  if (initialized) {
    return;
  }

  initialized = true;

  writeConsentCookie();
  document.addEventListener("termlyDataUpdate", writeConsentCookie);
  registerPurgeHook(purgeDenied);

  await migrateImmediate();
  ready(async () => {
    try {
      await migrateConsentGated();
    } catch (error) {
      console.error(error);
    }
  });
};
